/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const P = globalThis, q = P.ShadowRoot && (P.ShadyCSS === void 0 || P.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, rt = Symbol(), F = /* @__PURE__ */ new WeakMap();
let ht = class {
  constructor(t, e, i) {
    if (this._$cssResult$ = !0, i !== rt) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = t, this.t = e;
  }
  get styleSheet() {
    let t = this.o;
    const e = this.t;
    if (q && t === void 0) {
      const i = e !== void 0 && e.length === 1;
      i && (t = F.get(e)), t === void 0 && ((this.o = t = new CSSStyleSheet()).replaceSync(this.cssText), i && F.set(e, t));
    }
    return t;
  }
  toString() {
    return this.cssText;
  }
};
const lt = (s) => new ht(typeof s == "string" ? s : s + "", void 0, rt), ut = (s, t) => {
  if (q) s.adoptedStyleSheets = t.map(((e) => e instanceof CSSStyleSheet ? e : e.styleSheet));
  else for (const e of t) {
    const i = document.createElement("style"), r = P.litNonce;
    r !== void 0 && i.setAttribute("nonce", r), i.textContent = e.cssText, s.appendChild(i);
  }
}, j = q ? (s) => s : (s) => s instanceof CSSStyleSheet ? ((t) => {
  let e = "";
  for (const i of t.cssRules) e += i.cssText;
  return lt(e);
})(s) : s;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: ct, defineProperty: pt, getOwnPropertyDescriptor: bt, getOwnPropertyNames: ft, getOwnPropertySymbols: mt, getPrototypeOf: vt } = Object, U = globalThis, W = U.trustedTypes, kt = W ? W.emptyScript : "", gt = U.reactiveElementPolyfillSupport, S = (s, t) => s, H = { toAttribute(s, t) {
  switch (t) {
    case Boolean:
      s = s ? kt : null;
      break;
    case Object:
    case Array:
      s = s == null ? s : JSON.stringify(s);
  }
  return s;
}, fromAttribute(s, t) {
  let e = s;
  switch (t) {
    case Boolean:
      e = s !== null;
      break;
    case Number:
      e = s === null ? null : Number(s);
      break;
    case Object:
    case Array:
      try {
        e = JSON.parse(s);
      } catch {
        e = null;
      }
  }
  return e;
} }, z = (s, t) => !ct(s, t), K = { attribute: !0, type: String, converter: H, reflect: !1, useDefault: !1, hasChanged: z };
Symbol.metadata ??= Symbol("metadata"), U.litPropertyMetadata ??= /* @__PURE__ */ new WeakMap();
class w extends HTMLElement {
  static addInitializer(t) {
    this._$Ei(), (this.l ??= []).push(t);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(t, e = K) {
    if (e.state && (e.attribute = !1), this._$Ei(), this.prototype.hasOwnProperty(t) && ((e = Object.create(e)).wrapped = !0), this.elementProperties.set(t, e), !e.noAccessor) {
      const i = Symbol(), r = this.getPropertyDescriptor(t, i, e);
      r !== void 0 && pt(this.prototype, t, r);
    }
  }
  static getPropertyDescriptor(t, e, i) {
    const { get: r, set: n } = bt(this.prototype, t) ?? { get() {
      return this[e];
    }, set(o) {
      this[e] = o;
    } };
    return { get: r, set(o) {
      const d = r?.call(this);
      n?.call(this, o), this.requestUpdate(t, d, i);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(t) {
    return this.elementProperties.get(t) ?? K;
  }
  static _$Ei() {
    if (this.hasOwnProperty(S("elementProperties"))) return;
    const t = vt(this);
    t.finalize(), t.l !== void 0 && (this.l = [...t.l]), this.elementProperties = new Map(t.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(S("finalized"))) return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(S("properties"))) {
      const e = this.properties, i = [...ft(e), ...mt(e)];
      for (const r of i) this.createProperty(r, e[r]);
    }
    const t = this[Symbol.metadata];
    if (t !== null) {
      const e = litPropertyMetadata.get(t);
      if (e !== void 0) for (const [i, r] of e) this.elementProperties.set(i, r);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [e, i] of this.elementProperties) {
      const r = this._$Eu(e, i);
      r !== void 0 && this._$Eh.set(r, e);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(t) {
    const e = [];
    if (Array.isArray(t)) {
      const i = new Set(t.flat(1 / 0).reverse());
      for (const r of i) e.unshift(j(r));
    } else t !== void 0 && e.push(j(t));
    return e;
  }
  static _$Eu(t, e) {
    const i = e.attribute;
    return i === !1 ? void 0 : typeof i == "string" ? i : typeof t == "string" ? t.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    this._$ES = new Promise(((t) => this.enableUpdating = t)), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), this.constructor.l?.forEach(((t) => t(this)));
  }
  addController(t) {
    (this._$EO ??= /* @__PURE__ */ new Set()).add(t), this.renderRoot !== void 0 && this.isConnected && t.hostConnected?.();
  }
  removeController(t) {
    this._$EO?.delete(t);
  }
  _$E_() {
    const t = /* @__PURE__ */ new Map(), e = this.constructor.elementProperties;
    for (const i of e.keys()) this.hasOwnProperty(i) && (t.set(i, this[i]), delete this[i]);
    t.size > 0 && (this._$Ep = t);
  }
  createRenderRoot() {
    const t = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
    return ut(t, this.constructor.elementStyles), t;
  }
  connectedCallback() {
    this.renderRoot ??= this.createRenderRoot(), this.enableUpdating(!0), this._$EO?.forEach(((t) => t.hostConnected?.()));
  }
  enableUpdating(t) {
  }
  disconnectedCallback() {
    this._$EO?.forEach(((t) => t.hostDisconnected?.()));
  }
  attributeChangedCallback(t, e, i) {
    this._$AK(t, i);
  }
  _$ET(t, e) {
    const i = this.constructor.elementProperties.get(t), r = this.constructor._$Eu(t, i);
    if (r !== void 0 && i.reflect === !0) {
      const n = (i.converter?.toAttribute !== void 0 ? i.converter : H).toAttribute(e, i.type);
      this._$Em = t, n == null ? this.removeAttribute(r) : this.setAttribute(r, n), this._$Em = null;
    }
  }
  _$AK(t, e) {
    const i = this.constructor, r = i._$Eh.get(t);
    if (r !== void 0 && this._$Em !== r) {
      const n = i.getPropertyOptions(r), o = typeof n.converter == "function" ? { fromAttribute: n.converter } : n.converter?.fromAttribute !== void 0 ? n.converter : H;
      this._$Em = r;
      const d = o.fromAttribute(e, n.type);
      this[r] = d ?? this._$Ej?.get(r) ?? d, this._$Em = null;
    }
  }
  requestUpdate(t, e, i) {
    if (t !== void 0) {
      const r = this.constructor, n = this[t];
      if (i ??= r.getPropertyOptions(t), !((i.hasChanged ?? z)(n, e) || i.useDefault && i.reflect && n === this._$Ej?.get(t) && !this.hasAttribute(r._$Eu(t, i)))) return;
      this.C(t, e, i);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$EP());
  }
  C(t, e, { useDefault: i, reflect: r, wrapped: n }, o) {
    i && !(this._$Ej ??= /* @__PURE__ */ new Map()).has(t) && (this._$Ej.set(t, o ?? e ?? this[t]), n !== !0 || o !== void 0) || (this._$AL.has(t) || (this.hasUpdated || i || (e = void 0), this._$AL.set(t, e)), r === !0 && this._$Em !== t && (this._$Eq ??= /* @__PURE__ */ new Set()).add(t));
  }
  async _$EP() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (e) {
      Promise.reject(e);
    }
    const t = this.scheduleUpdate();
    return t != null && await t, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    if (!this.isUpdatePending) return;
    if (!this.hasUpdated) {
      if (this.renderRoot ??= this.createRenderRoot(), this._$Ep) {
        for (const [r, n] of this._$Ep) this[r] = n;
        this._$Ep = void 0;
      }
      const i = this.constructor.elementProperties;
      if (i.size > 0) for (const [r, n] of i) {
        const { wrapped: o } = n, d = this[r];
        o !== !0 || this._$AL.has(r) || d === void 0 || this.C(r, void 0, n, d);
      }
    }
    let t = !1;
    const e = this._$AL;
    try {
      t = this.shouldUpdate(e), t ? (this.willUpdate(e), this._$EO?.forEach(((i) => i.hostUpdate?.())), this.update(e)) : this._$EM();
    } catch (i) {
      throw t = !1, this._$EM(), i;
    }
    t && this._$AE(e);
  }
  willUpdate(t) {
  }
  _$AE(t) {
    this._$EO?.forEach(((e) => e.hostUpdated?.())), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(t)), this.updated(t);
  }
  _$EM() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(t) {
    return !0;
  }
  update(t) {
    this._$Eq &&= this._$Eq.forEach(((e) => this._$ET(e, this[e]))), this._$EM();
  }
  updated(t) {
  }
  firstUpdated(t) {
  }
}
w.elementStyles = [], w.shadowRootOptions = { mode: "open" }, w[S("elementProperties")] = /* @__PURE__ */ new Map(), w[S("finalized")] = /* @__PURE__ */ new Map(), gt?.({ ReactiveElement: w }), (U.reactiveElementVersions ??= []).push("2.1.1");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const D = globalThis, L = D.trustedTypes, J = L ? L.createPolicy("lit-html", { createHTML: (s) => s }) : void 0, nt = "$lit$", k = `lit$${Math.random().toFixed(9).slice(2)}$`, st = "?" + k, _t = `<${st}>`, $ = document, C = () => $.createComment(""), I = (s) => s === null || typeof s != "object" && typeof s != "function", G = Array.isArray, yt = (s) => G(s) || typeof s?.[Symbol.iterator] == "function", O = `[ 	
\f\r]`, E = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, Z = /-->/g, Q = />/g, _ = RegExp(`>|${O}(?:([^\\s"'>=/]+)(${O}*=${O}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), X = /'/g, Y = /"/g, ot = /^(?:script|style|textarea|title)$/i, x = Symbol.for("lit-noChange"), u = Symbol.for("lit-nothing"), tt = /* @__PURE__ */ new WeakMap(), y = $.createTreeWalker($, 129);
function at(s, t) {
  if (!G(s) || !s.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return J !== void 0 ? J.createHTML(t) : t;
}
const $t = (s, t) => {
  const e = s.length - 1, i = [];
  let r, n = t === 2 ? "<svg>" : t === 3 ? "<math>" : "", o = E;
  for (let d = 0; d < e; d++) {
    const a = s[d];
    let l, c, h = -1, m = 0;
    for (; m < a.length && (o.lastIndex = m, c = o.exec(a), c !== null); ) m = o.lastIndex, o === E ? c[1] === "!--" ? o = Z : c[1] !== void 0 ? o = Q : c[2] !== void 0 ? (ot.test(c[2]) && (r = RegExp("</" + c[2], "g")), o = _) : c[3] !== void 0 && (o = _) : o === _ ? c[0] === ">" ? (o = r ?? E, h = -1) : c[1] === void 0 ? h = -2 : (h = o.lastIndex - c[2].length, l = c[1], o = c[3] === void 0 ? _ : c[3] === '"' ? Y : X) : o === Y || o === X ? o = _ : o === Z || o === Q ? o = E : (o = _, r = void 0);
    const v = o === _ && s[d + 1].startsWith("/>") ? " " : "";
    n += o === E ? a + _t : h >= 0 ? (i.push(l), a.slice(0, h) + nt + a.slice(h) + k + v) : a + k + (h === -2 ? d : v);
  }
  return [at(s, n + (s[e] || "<?>") + (t === 2 ? "</svg>" : t === 3 ? "</math>" : "")), i];
};
class B {
  constructor({ strings: t, _$litType$: e }, i) {
    let r;
    this.parts = [];
    let n = 0, o = 0;
    const d = t.length - 1, a = this.parts, [l, c] = $t(t, e);
    if (this.el = B.createElement(l, i), y.currentNode = this.el.content, e === 2 || e === 3) {
      const h = this.el.content.firstChild;
      h.replaceWith(...h.childNodes);
    }
    for (; (r = y.nextNode()) !== null && a.length < d; ) {
      if (r.nodeType === 1) {
        if (r.hasAttributes()) for (const h of r.getAttributeNames()) if (h.endsWith(nt)) {
          const m = c[o++], v = r.getAttribute(h).split(k), T = /([.?@])?(.*)/.exec(m);
          a.push({ type: 1, index: n, name: T[2], strings: v, ctor: T[1] === "." ? xt : T[1] === "?" ? At : T[1] === "@" ? Et : N }), r.removeAttribute(h);
        } else h.startsWith(k) && (a.push({ type: 6, index: n }), r.removeAttribute(h));
        if (ot.test(r.tagName)) {
          const h = r.textContent.split(k), m = h.length - 1;
          if (m > 0) {
            r.textContent = L ? L.emptyScript : "";
            for (let v = 0; v < m; v++) r.append(h[v], C()), y.nextNode(), a.push({ type: 2, index: ++n });
            r.append(h[m], C());
          }
        }
      } else if (r.nodeType === 8) if (r.data === st) a.push({ type: 2, index: n });
      else {
        let h = -1;
        for (; (h = r.data.indexOf(k, h + 1)) !== -1; ) a.push({ type: 7, index: n }), h += k.length - 1;
      }
      n++;
    }
  }
  static createElement(t, e) {
    const i = $.createElement("template");
    return i.innerHTML = t, i;
  }
}
function A(s, t, e = s, i) {
  if (t === x) return t;
  let r = i !== void 0 ? e._$Co?.[i] : e._$Cl;
  const n = I(t) ? void 0 : t._$litDirective$;
  return r?.constructor !== n && (r?._$AO?.(!1), n === void 0 ? r = void 0 : (r = new n(s), r._$AT(s, e, i)), i !== void 0 ? (e._$Co ??= [])[i] = r : e._$Cl = r), r !== void 0 && (t = A(s, r._$AS(s, t.values), r, i)), t;
}
class wt {
  constructor(t, e) {
    this._$AV = [], this._$AN = void 0, this._$AD = t, this._$AM = e;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(t) {
    const { el: { content: e }, parts: i } = this._$AD, r = (t?.creationScope ?? $).importNode(e, !0);
    y.currentNode = r;
    let n = y.nextNode(), o = 0, d = 0, a = i[0];
    for (; a !== void 0; ) {
      if (o === a.index) {
        let l;
        a.type === 2 ? l = new M(n, n.nextSibling, this, t) : a.type === 1 ? l = new a.ctor(n, a.name, a.strings, this, t) : a.type === 6 && (l = new St(n, this, t)), this._$AV.push(l), a = i[++d];
      }
      o !== a?.index && (n = y.nextNode(), o++);
    }
    return y.currentNode = $, r;
  }
  p(t) {
    let e = 0;
    for (const i of this._$AV) i !== void 0 && (i.strings !== void 0 ? (i._$AI(t, i, e), e += i.strings.length - 2) : i._$AI(t[e])), e++;
  }
}
class M {
  get _$AU() {
    return this._$AM?._$AU ?? this._$Cv;
  }
  constructor(t, e, i, r) {
    this.type = 2, this._$AH = u, this._$AN = void 0, this._$AA = t, this._$AB = e, this._$AM = i, this.options = r, this._$Cv = r?.isConnected ?? !0;
  }
  get parentNode() {
    let t = this._$AA.parentNode;
    const e = this._$AM;
    return e !== void 0 && t?.nodeType === 11 && (t = e.parentNode), t;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(t, e = this) {
    t = A(this, t, e), I(t) ? t === u || t == null || t === "" ? (this._$AH !== u && this._$AR(), this._$AH = u) : t !== this._$AH && t !== x && this._(t) : t._$litType$ !== void 0 ? this.$(t) : t.nodeType !== void 0 ? this.T(t) : yt(t) ? this.k(t) : this._(t);
  }
  O(t) {
    return this._$AA.parentNode.insertBefore(t, this._$AB);
  }
  T(t) {
    this._$AH !== t && (this._$AR(), this._$AH = this.O(t));
  }
  _(t) {
    this._$AH !== u && I(this._$AH) ? this._$AA.nextSibling.data = t : this.T($.createTextNode(t)), this._$AH = t;
  }
  $(t) {
    const { values: e, _$litType$: i } = t, r = typeof i == "number" ? this._$AC(t) : (i.el === void 0 && (i.el = B.createElement(at(i.h, i.h[0]), this.options)), i);
    if (this._$AH?._$AD === r) this._$AH.p(e);
    else {
      const n = new wt(r, this), o = n.u(this.options);
      n.p(e), this.T(o), this._$AH = n;
    }
  }
  _$AC(t) {
    let e = tt.get(t.strings);
    return e === void 0 && tt.set(t.strings, e = new B(t)), e;
  }
  k(t) {
    G(this._$AH) || (this._$AH = [], this._$AR());
    const e = this._$AH;
    let i, r = 0;
    for (const n of t) r === e.length ? e.push(i = new M(this.O(C()), this.O(C()), this, this.options)) : i = e[r], i._$AI(n), r++;
    r < e.length && (this._$AR(i && i._$AB.nextSibling, r), e.length = r);
  }
  _$AR(t = this._$AA.nextSibling, e) {
    for (this._$AP?.(!1, !0, e); t !== this._$AB; ) {
      const i = t.nextSibling;
      t.remove(), t = i;
    }
  }
  setConnected(t) {
    this._$AM === void 0 && (this._$Cv = t, this._$AP?.(t));
  }
}
class N {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(t, e, i, r, n) {
    this.type = 1, this._$AH = u, this._$AN = void 0, this.element = t, this.name = e, this._$AM = r, this.options = n, i.length > 2 || i[0] !== "" || i[1] !== "" ? (this._$AH = Array(i.length - 1).fill(new String()), this.strings = i) : this._$AH = u;
  }
  _$AI(t, e = this, i, r) {
    const n = this.strings;
    let o = !1;
    if (n === void 0) t = A(this, t, e, 0), o = !I(t) || t !== this._$AH && t !== x, o && (this._$AH = t);
    else {
      const d = t;
      let a, l;
      for (t = n[0], a = 0; a < n.length - 1; a++) l = A(this, d[i + a], e, a), l === x && (l = this._$AH[a]), o ||= !I(l) || l !== this._$AH[a], l === u ? t = u : t !== u && (t += (l ?? "") + n[a + 1]), this._$AH[a] = l;
    }
    o && !r && this.j(t);
  }
  j(t) {
    t === u ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, t ?? "");
  }
}
class xt extends N {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(t) {
    this.element[this.name] = t === u ? void 0 : t;
  }
}
class At extends N {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(t) {
    this.element.toggleAttribute(this.name, !!t && t !== u);
  }
}
class Et extends N {
  constructor(t, e, i, r, n) {
    super(t, e, i, r, n), this.type = 5;
  }
  _$AI(t, e = this) {
    if ((t = A(this, t, e, 0) ?? u) === x) return;
    const i = this._$AH, r = t === u && i !== u || t.capture !== i.capture || t.once !== i.once || t.passive !== i.passive, n = t !== u && (i === u || r);
    r && this.element.removeEventListener(this.name, this, i), n && this.element.addEventListener(this.name, this, t), this._$AH = t;
  }
  handleEvent(t) {
    typeof this._$AH == "function" ? this._$AH.call(this.options?.host ?? this.element, t) : this._$AH.handleEvent(t);
  }
}
class St {
  constructor(t, e, i) {
    this.element = t, this.type = 6, this._$AN = void 0, this._$AM = e, this.options = i;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(t) {
    A(this, t);
  }
}
const Rt = D.litHtmlPolyfillSupport;
Rt?.(B, M), (D.litHtmlVersions ??= []).push("3.3.1");
const Ct = (s, t, e) => {
  const i = e?.renderBefore ?? t;
  let r = i._$litPart$;
  if (r === void 0) {
    const n = e?.renderBefore ?? null;
    i._$litPart$ = r = new M(t.insertBefore(C(), n), n, void 0, e ?? {});
  }
  return r._$AI(s), r;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const V = globalThis;
class R extends w {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    const t = super.createRenderRoot();
    return this.renderOptions.renderBefore ??= t.firstChild, t;
  }
  update(t) {
    const e = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(t), this._$Do = Ct(e, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    super.connectedCallback(), this._$Do?.setConnected(!0);
  }
  disconnectedCallback() {
    super.disconnectedCallback(), this._$Do?.setConnected(!1);
  }
  render() {
    return x;
  }
}
R._$litElement$ = !0, R.finalized = !0, V.litElementHydrateSupport?.({ LitElement: R });
const It = V.litElementPolyfillSupport;
It?.({ LitElement: R });
(V.litElementVersions ??= []).push("4.2.1");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Bt = { attribute: !0, type: String, converter: H, reflect: !1, hasChanged: z }, Mt = (s = Bt, t, e) => {
  const { kind: i, metadata: r } = e;
  let n = globalThis.litPropertyMetadata.get(r);
  if (n === void 0 && globalThis.litPropertyMetadata.set(r, n = /* @__PURE__ */ new Map()), i === "setter" && ((s = Object.create(s)).wrapped = !0), n.set(e.name, s), i === "accessor") {
    const { name: o } = e;
    return { set(d) {
      const a = t.get.call(this);
      t.set.call(this, d), this.requestUpdate(o, a, s);
    }, init(d) {
      return d !== void 0 && this.C(o, void 0, s, d), d;
    } };
  }
  if (i === "setter") {
    const { name: o } = e;
    return function(d) {
      const a = this[o];
      t.call(this, d), this.requestUpdate(o, a, s);
    };
  }
  throw Error("Unsupported decorator location: " + i);
};
function b(s) {
  return (t, e) => typeof e == "object" ? Mt(s, t, e) : ((i, r, n) => {
    const o = r.hasOwnProperty(n);
    return r.constructor.createProperty(n, i), o ? Object.getOwnPropertyDescriptor(r, n) : void 0;
  })(s, t, e);
}
const Tt = (s) => {
  class t extends s {
    constructor(...i) {
      super(...i), this._attachDeclarativeTemplate();
    }
    connectedCallback() {
      super.connectedCallback(), this.hydrate();
    }
    /* Lifecycle Hook to ensure that our components are reactive */
    attributeChangedCallback(i, r, n) {
      super.attributeChangedCallback(i, r, n), r !== n && this.updateAttributes();
    }
    /**
     * Attempts to use a declarative shadow DOM template if provided.
     * If used, this bypasses Lit's standard rendering.
     */
    _attachDeclarativeTemplate() {
      const i = this.constructor;
      if (!i.template) return;
      const r = document.createElement("template");
      r.innerHTML = i.template.trim();
      const n = r.content.firstChild;
      if (n && n.nodeName === "TEMPLATE" && n.getAttribute("shadowrootmode") === "open") {
        const o = n, d = this.attachShadow({ mode: "open" }), a = o.content.cloneNode(!0);
        d.appendChild(a), this.__hasDeclarativeShadowRoot = !0;
      }
    }
    hydrate() {
      this.shadowRoot && (this.updateAttributes(), this.setupEventHandling());
    }
    /* Those methods are called by the user of the mixin for a consistent hydration process */
    updateAttributes() {
      throw new Error("Method not implemented.");
    }
    setupEventHandling() {
      throw new Error("Method not implemented.");
    }
    /**
     * Override createRenderRoot to prevent Lit's standard rendering
     * if we've already attached a declarative shadow DOM template
     */
    createRenderRoot() {
      return this.__hasDeclarativeShadowRoot ? this.shadowRoot : super.createRenderRoot();
    }
  }
  return t;
}, Pt = (s) => {
  class t extends s {
    connectedCallback() {
      super.connectedCallback(), this._bindDeclarativeEvents();
    }
    /**
     * Finds all elements with @event attributes and binds them to the corresponding methods
     */
    _bindDeclarativeEvents() {
      const i = this.shadowRoot;
      if (!i) return;
      const r = document.createTreeWalker(
        i,
        NodeFilter.SHOW_ELEMENT
      );
      let n = r.nextNode();
      for (; n; )
        Array.from(n.attributes).forEach((o) => {
          if (o.name.startsWith("@")) {
            const d = o.name.slice(1), a = o.value, l = this[a];
            typeof l == "function" ? n.addEventListener(d, l.bind(this)) : console.warn(
              `${this.localName}: no method '${a}' for event '${d}'`
            );
          }
        }), n = r.nextNode();
    }
  }
  return t;
}, Ht = (s) => Pt(Tt(s));
let et = 0;
const it = (s) => (et += 1, `${s}-${et}`);
var Lt = Object.defineProperty, f = (s, t, e, i) => {
  for (var r = void 0, n = s.length - 1, o; n >= 0; n--)
    (o = s[n]) && (r = o(t, e, r) || r);
  return r && Lt(t, e, r), r;
};
const dt = `<template shadowrootmode="open"><style>/*! tailwindcss v4.1.14 | MIT License | https://tailwindcss.com */
@layer properties{@supports ((-webkit-hyphens:none) and (not (margin-trim:inline))) or ((-moz-orient:inline) and (not (color:rgb(from red r g b)))){*,:before,:after,::backdrop{--tw-rotate-x:initial;--tw-rotate-y:initial;--tw-rotate-z:initial;--tw-skew-x:initial;--tw-skew-y:initial;--tw-blur:initial;--tw-brightness:initial;--tw-contrast:initial;--tw-grayscale:initial;--tw-hue-rotate:initial;--tw-invert:initial;--tw-opacity:initial;--tw-saturate:initial;--tw-sepia:initial;--tw-drop-shadow:initial;--tw-drop-shadow-color:initial;--tw-drop-shadow-alpha:100%;--tw-drop-shadow-size:initial}}}.visible{visibility:visible}.static{position:static}.isolate{isolation:isolate}.my-8{margin-block:var(--bdk-space-8,5.33333rem)}.mb-2{margin-bottom:var(--bdk-space-2,1.33333rem)}.block{display:block}.contents{display:contents}.flex{display:flex}.inline{display:inline}.h-full{height:100%}.max-w-\\[500px\\]{max-width:500px}.transform{transform:var(--tw-rotate-x,)var(--tw-rotate-y,)var(--tw-rotate-z,)var(--tw-skew-x,)var(--tw-skew-y,)}.flex-col{flex-direction:column}.gap-2{gap:var(--bdk-space-2,1.33333rem)}.gap-4{gap:var(--bdk-space-4,2.66667rem)}.overflow-y-auto{overflow-y:auto}.bdk-tw-heading-s{letter-spacing:.05rem;text-transform:uppercase;font-family:Eduard Breuninger;font-size:2.66667rem;font-weight:500;line-height:4rem}.filter{filter:var(--tw-blur,)var(--tw-brightness,)var(--tw-contrast,)var(--tw-grayscale,)var(--tw-hue-rotate,)var(--tw-invert,)var(--tw-saturate,)var(--tw-sepia,)var(--tw-drop-shadow,)}:host{gap:var(--bdk-space-4);box-sizing:border-box;flex-direction:column;display:inline-flex}.bdk-radio-button{--bdk-radio-button-input-border-width:1px;--bdk-radio-button-input-border-color:var(--bdk-coal-100,#1a1a1a);--bdk-radio-button-marker-background-color:var(--bdk-coal-100,#1a1a1a);--bdk-radio-button-marker-transform:scale(0);--bdk-radio-button-text-color:var(--bdk-coal-100,#1a1a1a);align-items:center;gap:var(--bdk-space-2,1.33333rem);display:flex}@media screen and (pointer:fine){.bdk-radio-button:hover{--bdk-radio-button-input-border-color:var(--bdk-red-hover-light,#cb0d33);--bdk-radio-button-input-border-width:2px;--bdk-radio-button-text-color:var(--bdk-red-hover-light,#cb0d33);--bdk-radio-button-marker-background-color:var(--bdk-red-hover-light,#cb0d33);cursor:pointer}}.bdk-radio-button:has(input:disabled){--bdk-radio-button-input-border-color:var(--bdk-pebble,#e0dfde);--bdk-radio-button-text-color:var(--bdk-smoke,#928d8b);--bdk-radio-button-marker-background-color:var(--bdk-pebble,#e0dfde);cursor:auto}.bdk-radio-button[multiline]{align-items:flex-start}@media screen and (pointer:fine){.bdk-radio-button:has(input:disabled):hover{--bdk-radio-button-input-border-width:1px;--bdk-radio-button-input-border-color:var(--bdk-pebble,#e0dfde);--bdk-radio-button-text-color:var(--bdk-smoke,#928d8b);--bdk-radio-button-marker-background-color:var(--bdk-pebble,#e0dfde)}}.bdk-radio-button:has(input[aria-invalid=true]){--bdk-radio-button-input-border-color:var(--bdk-error,#b20c16);--bdk-radio-button-text-color:var(--bdk-error,#b20c16)}@media screen and (pointer:fine){.bdk-radio-button:has(input[aria-invalid=true]):hover{--bdk-radio-button-input-border-color:var(--bdk-red-hover-light,#cb0d33);--bdk-radio-button-text-color:var(--bdk-red-hover-light,#cb0d33)}}.bdk-radio-button:has(input:checked){--bdk-radio-button-marker-transform:scale(1)}:host([multiline]) .bdk-radio-button{align-items:flex-start}:host([inverted]) .bdk-radio-button{--bdk-radio-button-input-border-color:var(--bdk-snow-100);--bdk-radio-button-text-color:var(--bdk-snow-100);border-radius:var(--bdk-space-2);margin:calc(-1*var(--bdk-space-2));padding:var(--bdk-space-2)}:host([inverted]) .bdk-radio-button:has(input:checked){--bdk-radio-button-marker-background-color:var(--bdk-snow-100)}@media screen and (pointer:fine){:host([inverted]) .bdk-radio-button:has(input:checked:hover:not(:disabled)){--bdk-radio-button-input-border-color:var(--bdk-coal-100);--bdk-radio-button-input-background-color:var(--bdk-snow-100);--bdk-radio-button-marker-background-color:var(--bdk-coal-100)}}:host([inverted]) .bdk-radio-button:has(input:active:not(:disabled)){--bdk-radio-button-input-border-color:var(--bdk-coal-100);--bdk-radio-button-input-background-color:var(--bdk-snow-100)}:host([inverted]) .bdk-radio-button:has(input:checked:active:not(:disabled)){--bdk-radio-button-marker-background-color:var(--bdk-coal-100)}:host([inverted]) .bdk-radio-button:has(input[aria-invalid=true]:not(:disabled)){--bdk-radio-button-input-border-color:var(--bdk-error-background)}:host([inverted]) .bdk-radio-button:has(input[aria-invalid=true]:not(:disabled)):not(:hover){--bdk-radio-button-input-border-width:var(--bdk-space-1)}@media screen and (pointer:fine){:host([inverted]) .bdk-radio-button:has(input[aria-invalid=true]:not(:disabled)):hover{--bdk-radio-button-text-color:var(--bdk-snow-100);--bdk-radio-button-input-background-color:transparent}}:host([inverted]) .bdk-radio-button:has(input:disabled){--bdk-radio-button-input-border-color:var(--bdk-snow-25,#ffffff40);--bdk-radio-button-text-color:var(--bdk-snow-50,#ffffff80)}:host([inverted]) .bdk-radio-button:has(input:disabled:checked){--bdk-radio-button-marker-background-color:var(--bdk-snow-50,#ffffff80)}@media screen and (pointer:fine){:host([inverted]) .bdk-radio-button:has(input:disabled:hover){--bdk-radio-button-input-background-color:transparent}}.bdk-radio-button--input{width:var(--bdk-space-5,3.33333rem);height:var(--bdk-space-5,3.33333rem);background-color:var(--bdk-snow-100,#fff);border:solid var(--bdk-radio-button-input-border-width)var(--bdk-radio-button-input-border-color);border-radius:var(--bdk-radius-pill,166.5rem);-webkit-appearance:none;-moz-appearance:none;appearance:none;flex-shrink:0;justify-content:center;align-items:center;display:flex}.bdk-radio-button--input:before{content:"";width:var(--bdk-space-3,2rem);height:var(--bdk-space-3,2rem);background-color:var(--bdk-radio-button-marker-background-color);border-radius:var(--bdk-radius-pill,166.5rem);transform:var(--bdk-radio-button-marker-transform);transition:transform var(--bdk-transition-duration-default,.2s)ease,background-color var(--bdk-transition-duration-default,.2s)ease}@media only screen and (max-resolution:1.5x){.bdk-radio-button--input{width:20px;height:20px}.bdk-radio-button--input:before{width:12px;height:12px}}.bdk-radio-button--input:not(:disabled):focus-visible{outline:2px solid var(--bdk-focus-color);outline-offset:2px;box-shadow:0 0 0 3px var(--bdk-focus-background-color)}.bdk-radio-button--input:not(:disabled)[inverted]:focus-visible{outline:2px solid var(--bdk-focus-color-inverted);outline-offset:2px;box-shadow:0 0 0 3px var(--bdk-focus-background-color-inverted)}@media (prefers-reduced-motion:reduce){.bdk-radio-button--input:before{transition:none}}.bdk-radio-button--input{cursor:pointer;background-color:var(--bdk-radio-button-input-background-color,transparent)}.bdk-radio-button--text{font-family:var(--bdk-text-m-font-family,"Eduard Breuninger",Helvetica,Arial,sans-serif);font-size:var(--bdk-text-m-font-size,2.66667rem);font-weight:var(--bdk-text-m-font-weight,400);letter-spacing:var(--bdk-text-m-letter-spacing,.066667rem);line-height:var(--bdk-text-m-line-height,4rem);-webkit-text-decoration:var(--bdk-text-m-text-decoration,none);-webkit-text-decoration:var(--bdk-text-m-text-decoration,none);text-decoration:var(--bdk-text-m-text-decoration,none);text-transform:var(--bdk-text-m-text-transform,inherit);color:var(--bdk-radio-button-text-color);transition:color var(--bdk-transition-duration-default,.2s)ease}@media (prefers-reduced-motion:reduce){.bdk-radio-button--text{transition:none}}.bdk-radio-button--text{width:100%}:host([size=small]) .bdk-radio-button--text{font-family:var(--bdk-text-s-font-family,"Eduard Breuninger",Helvetica,Arial,sans-serif);font-size:var(--bdk-text-s-font-size,2.33333rem);font-weight:var(--bdk-text-s-font-weight,400);letter-spacing:var(--bdk-text-s-letter-spacing,.05rem);line-height:var(--bdk-text-s-line-height,3.66667rem);-webkit-text-decoration:var(--bdk-text-s-text-decoration,none);-webkit-text-decoration:var(--bdk-text-s-text-decoration,none);text-decoration:var(--bdk-text-s-text-decoration,none);text-transform:var(--bdk-text-s-text-transform,inherit);color:var(--bdk-radio-button-text-color);transition:color var(--bdk-transition-duration-default,.2s)ease}@media (prefers-reduced-motion:reduce){:host([size=small]) .bdk-radio-button--text{transition:none}}.bdk-radio-button--hint{font-family:var(--bdk-text-m-font-family,"Eduard Breuninger",Helvetica,Arial,sans-serif);font-size:var(--bdk-text-m-font-size,2.66667rem);font-weight:var(--bdk-text-m-font-weight,400);letter-spacing:var(--bdk-text-m-letter-spacing,.066667rem);line-height:var(--bdk-text-m-line-height,4rem);-webkit-text-decoration:var(--bdk-text-m-text-decoration,none);-webkit-text-decoration:var(--bdk-text-m-text-decoration,none);text-decoration:var(--bdk-text-m-text-decoration,none);text-transform:var(--bdk-text-m-text-transform,inherit);color:var(--bdk-radio-button-text-color);transition:color var(--bdk-transition-duration-default,.2s)ease}@media (prefers-reduced-motion:reduce){.bdk-radio-button--hint{transition:none}}.bdk-radio-button--hint{margin-left:calc(var(--bdk-space-8) + 3px)}.bdk-radio-button--hint:empty{display:none}:host([size=small]) .bdk-radio-button--hint{font-family:var(--bdk-text-s-font-family,"Eduard Breuninger",Helvetica,Arial,sans-serif);font-size:var(--bdk-text-s-font-size,2.33333rem);font-weight:var(--bdk-text-s-font-weight,400);letter-spacing:var(--bdk-text-s-letter-spacing,.05rem);line-height:var(--bdk-text-s-line-height,3.66667rem);-webkit-text-decoration:var(--bdk-text-s-text-decoration,none);-webkit-text-decoration:var(--bdk-text-s-text-decoration,none);text-decoration:var(--bdk-text-s-text-decoration,none);text-transform:var(--bdk-text-s-text-transform,inherit);color:var(--bdk-radio-button-text-color);transition:color var(--bdk-transition-duration-default,.2s)ease}@media (prefers-reduced-motion:reduce){:host([size=small]) .bdk-radio-button--hint{transition:none}}@property --tw-rotate-x{syntax:"*";inherits:false}@property --tw-rotate-y{syntax:"*";inherits:false}@property --tw-rotate-z{syntax:"*";inherits:false}@property --tw-skew-x{syntax:"*";inherits:false}@property --tw-skew-y{syntax:"*";inherits:false}@property --tw-blur{syntax:"*";inherits:false}@property --tw-brightness{syntax:"*";inherits:false}@property --tw-contrast{syntax:"*";inherits:false}@property --tw-grayscale{syntax:"*";inherits:false}@property --tw-hue-rotate{syntax:"*";inherits:false}@property --tw-invert{syntax:"*";inherits:false}@property --tw-opacity{syntax:"*";inherits:false}@property --tw-saturate{syntax:"*";inherits:false}@property --tw-sepia{syntax:"*";inherits:false}@property --tw-drop-shadow{syntax:"*";inherits:false}@property --tw-drop-shadow-color{syntax:"*";inherits:false}@property --tw-drop-shadow-alpha{syntax:"<percentage>";inherits:false;initial-value:100%}@property --tw-drop-shadow-size{syntax:"*";inherits:false}
</style>
  
  <label class="bdk-radio-button">
    <input
      type="radio"
      class="bdk-radio-button--input"
      role="radio"
      aria-checked="false"
    />
    <span class="bdk-radio-button--text">
      <slot></slot>
    </span>
  </label>
  <div class="bdk-radio-button--hint">
    <slot name="hint"></slot>
  </div>
</template>`;
function Nt() {
  return dt;
}
var g;
const p = (g = class extends Ht(R) {
  constructor() {
    super(...arguments), this.internals = this.attachInternals(), this._formChangeListener = (t) => {
      const e = t.target;
      e.type === "radio" && e.name === this.name && e !== this.input && e.checked && (this.checked = !1, this.input && (this.input.checked = !1), this.internals.setFormValue(null), this._updateValidationStateInGroup());
    }, this._hasFormListener = !1, this.value = "on", this.disabled = !1, this.name = "", this.readonly = !1, this.required = !1, this.size = "medium", this.checked = !1, this.invalid = !1, this.inverted = !1, this.inputId = it("bdk-radio-button"), this.hintId = it("bdk-radio-button-hint");
  }
  get form() {
    return this.internals.form;
  }
  /* Getter seems to work after connecting, @query does not */
  get input() {
    return this.shadowRoot?.querySelector("input");
  }
  get hint() {
    return this.shadowRoot?.querySelector(
      ".bdk-radio-button--hint"
    );
  }
  get label() {
    return this.shadowRoot?.querySelector("label");
  }
  firstUpdated(t) {
    super.firstUpdated(t), this.input && (this.internals.setFormValue && this.checked && this.internals.setFormValue(this.value), this._updateValidationState(), this._setupFormRadioListeners(), this._updateRadioGroupTabIndex(), this._updateRequiredAttributesInGroup());
  }
  connectedCallback() {
    super.connectedCallback(), this._setupFormRadioListeners();
  }
  disconnectedCallback() {
    super.disconnectedCallback(), this._cleanupFormRadioListeners();
  }
  focusInput() {
    this.input.focus();
  }
  _setupFormRadioListeners() {
    if (!this.name) return;
    const t = this.internals.form || this.closest("form");
    t && !this._hasFormListener && (t.addEventListener("change", this._formChangeListener), this._hasFormListener = !0);
  }
  _cleanupFormRadioListeners() {
    if (!this.name) return;
    const t = this.internals.form || this.closest("form");
    t && this._hasFormListener && (t.removeEventListener("change", this._formChangeListener), this._hasFormListener = !1);
  }
  update(t) {
    super.update(t), this.input && (t.has("checked") && (this.input.checked = this.checked, this.input.setAttribute("aria-checked", this.checked.toString()), this._updateValidationState(), this._updateRadioGroupTabIndex()), t.has("value") && (this.input.value = this.value, this._updateValidationState()), t.has("name") && (this.input.name = this.name, this._cleanupFormRadioListeners(), this._setupFormRadioListeners(), this._updateRequiredAttributesInGroup()), t.has("required") && this._updateRequiredAttributesInGroup());
  }
  updateAttributes() {
    if (!this.input) throw new Error("Input element not found");
    if (!this.label) throw new Error("Label element not found");
    if (!this.hint) throw new Error("Hint element not found");
    this.input.setAttribute("id", this.inputId), this.input.setAttribute("aria-describedby", this.hintId), this.hint.setAttribute("id", this.hintId), this.label.setAttribute("for", this.inputId), this.required && this._isFirstRadioInGroup() ? this.input.setAttribute("required", "") : this.input.removeAttribute("required"), this.disabled ? this.input.setAttribute("disabled", "") : this.input.removeAttribute("disabled"), this.inverted ? this.input.setAttribute("inverted", "") : this.input.removeAttribute("inverted"), this.input.setAttribute("value", this.value), this.input.setAttribute("name", this.name), this.invalid ? this.input.setAttribute("aria-invalid", "true") : this.input.removeAttribute("aria-invalid"), this.input.setAttribute("aria-checked", this.checked.toString());
  }
  setupEventHandling() {
    if (!this.input) throw new Error("Input element not found");
    this.input.addEventListener("change", this.handleChange.bind(this)), this.input.addEventListener("click", this.handleClick.bind(this)), this.input.addEventListener("keydown", this.handleKeydown.bind(this)), this.input.addEventListener("focus", this.handleFocus.bind(this));
  }
  handleClick(t) {
    this.readonly && t.preventDefault();
  }
  handleKeydown(t) {
    if (!(this.readonly || this.disabled))
      switch (t.key) {
        case " ":
        case "Space":
          t.preventDefault(), this._selectRadioButton();
          break;
        case "ArrowDown":
        case "ArrowRight":
          t.preventDefault(), this._focusNextRadioButton();
          break;
        case "ArrowUp":
        case "ArrowLeft":
          t.preventDefault(), this._focusPreviousRadioButton();
          break;
      }
  }
  handleFocus() {
    this._updateRadioGroupTabIndex();
  }
  handleChange() {
    if (this.readonly) return;
    const { checked: t } = this.input;
    this.checked = t, this.internals.setFormValue(this.checked ? this.value : null), this.checked && this.name && this._uncheckOtherRadioButtons(), this._updateValidationStateInGroup(), this.dispatchEvent(
      new Event("change", {
        bubbles: !0,
        composed: !0
      })
    );
  }
  _uncheckOtherRadioButtons() {
    if (!this.name) return;
    const t = this.internals.form || this.closest("form");
    if (!t) return;
    this._findRadioButtonsWithSameName(t).forEach((i) => {
      i !== this && this._isRadioChecked(i) && this._uncheckRadio(i);
    });
  }
  _findRadioButtonsWithSameName(t) {
    const e = [], i = t.querySelectorAll(`[name="${this.name}"]`);
    return e.push(...Array.from(i)), e;
  }
  _isRadioChecked(t) {
    return t instanceof HTMLInputElement, t.checked;
  }
  _uncheckRadio(t) {
    t instanceof HTMLInputElement ? (t.checked = !1, t.dispatchEvent(new Event("change", { bubbles: !0 }))) : (t.checked = !1, t.input && (t.input.checked = !1), t.internals.setFormValue(null), t.dispatchEvent(
      new Event("change", { bubbles: !0, composed: !0 })
    ));
  }
  _selectRadioButton() {
    this.input && (this.input.checked = !0, this.handleChange());
  }
  _focusNextRadioButton() {
    const t = this._getRadioButtonsInGroup(), e = t.findIndex(
      (n) => n === this || n instanceof HTMLInputElement && n === this.input
    );
    if (e === -1) return;
    const i = (e + 1) % t.length, r = t[i];
    this._focusRadioButton(r);
  }
  _focusPreviousRadioButton() {
    const t = this._getRadioButtonsInGroup(), e = t.findIndex(
      (n) => n === this || n instanceof HTMLInputElement && n === this.input
    );
    if (e === -1) return;
    const i = e === 0 ? t.length - 1 : e - 1, r = t[i];
    this._focusRadioButton(r);
  }
  _getRadioButtonsInGroup() {
    if (!this.name) return [this];
    const t = this.internals.form || this.closest("form") || document;
    return this._findRadioButtonsWithSameName(t);
  }
  _focusRadioButton(t) {
    t instanceof HTMLInputElement ? t.focus() : t.input && t.input.focus();
  }
  _updateRadioGroupTabIndex() {
    if (!this.name) return;
    const t = this._getRadioButtonsInGroup(), e = t.find(
      (i) => this._isRadioChecked(i)
    );
    t.forEach((i) => {
      const r = i instanceof HTMLInputElement ? i : i.input;
      r && (e ? r.tabIndex = i === e ? 0 : -1 : r.tabIndex = i === t[0] ? 0 : -1);
    });
  }
  _isFirstRadioInGroup() {
    if (!this.name) return !0;
    const t = this._getRadioButtonsInGroup();
    return t.length > 0 && t[0] === this;
  }
  _updateRequiredAttributesInGroup() {
    if (!this.name) return;
    this._getRadioButtonsInGroup().forEach((e, i) => {
      const r = e instanceof HTMLInputElement ? e : e.input, n = e instanceof HTMLInputElement ? e.hasAttribute("required") : e.required;
      !r || !n || (i === 0 ? r.setAttribute("required", "") : r.removeAttribute("required"));
    });
  }
  _updateValidationState() {
    if (!this.input || !this.internals.setValidity) return;
    if (!this._isGroupRequired()) {
      this.internals.setValidity(
        this.input.validity,
        this.input.validationMessage,
        this.input
      );
      return;
    }
    if (!this._isGroupValid() && this.input.validationMessage !== "") {
      this.internals.setValidity(
        { valueMissing: !0 },
        this.input.validationMessage,
        this.input
      );
      return;
    }
    this.internals.setValidity({});
  }
  _updateValidationStateInGroup() {
    if (!this.name) return;
    this._getRadioButtonsInGroup().forEach((e) => {
      e instanceof g && e._updateValidationState();
    });
  }
  _isGroupRequired() {
    return this.name ? this._getRadioButtonsInGroup().some((e) => e instanceof HTMLInputElement ? e.hasAttribute("required") : e.required) : this.required;
  }
  _isGroupValid() {
    return this.name ? this._getRadioButtonsInGroup().some((e) => this._isRadioChecked(e)) : this.checked || !this.required;
  }
}, g.template = dt, g.formAssociated = !0, g);
f([
  b({ reflect: !0 })
], p.prototype, "value");
f([
  b({ type: Boolean, reflect: !0 })
], p.prototype, "disabled");
f([
  b({ reflect: !0, type: String })
], p.prototype, "name");
f([
  b({ type: Boolean, reflect: !0 })
], p.prototype, "readonly");
f([
  b({ type: Boolean, reflect: !0 })
], p.prototype, "required");
f([
  b({ type: String, reflect: !0 })
], p.prototype, "size");
f([
  b({ type: Boolean, reflect: !0 })
], p.prototype, "checked");
f([
  b({ type: Boolean, reflect: !0 })
], p.prototype, "invalid");
f([
  b({ type: Boolean, reflect: !0 })
], p.prototype, "inverted");
f([
  b({ type: String, reflect: !1, attribute: "input-id" })
], p.prototype, "inputId");
f([
  b({ type: String, reflect: !1, attribute: "hint-id" })
], p.prototype, "hintId");
let Ot = p;
export {
  Ot as BdkRadioButton,
  dt as default,
  Nt as dsd
};
